import { useState, useEffect, useRef } from "react";
import { MODEL_DISPLAY_NAMES } from "../constants/modelMeta";

const ENSEMBLE_MODELS = ["zscore", "rsf", "pdf", "lof", "benford", "montecarlo"];

const MODEL_OPTIONS = [
  { label: MODEL_DISPLAY_NAMES.zscore, value: "zscore" },
  { label: MODEL_DISPLAY_NAMES.benford, value: "benford" },
  { label: MODEL_DISPLAY_NAMES.rsf, value: "rsf" },
  { label: MODEL_DISPLAY_NAMES.lof, value: "lof" },
  { label: MODEL_DISPLAY_NAMES.pdf, value: "pdf" },
  { label: MODEL_DISPLAY_NAMES.montecarlo, value: "montecarlo" },
  { label: MODEL_DISPLAY_NAMES.sampling, value: "sampling" },
  { label: MODEL_DISPLAY_NAMES.ensemble, value: "ensemble" },
];

const namesFromColumns = (cols) => {
  if (!cols) return [];
  if (cols.length === 0) return [];
  // if items are strings, return as-is; if objects, map .name (fallback to toString)
  return typeof cols[0] === "string"
    ? cols
    : cols.map((c) => (typeof c === "string" ? c : c.name || String(c)));
};

export const useAnalyticsConfiguration = (columns = [], numericColumns, allColumns) => {
  const hasRestoredRef = useRef(false);

  const columnNames = namesFromColumns(columns.map(c => (typeof c === 'string' ? c : c.name ? c : c)));
  const allNames = namesFromColumns(allColumns && allColumns.length ? allColumns : columnNames);
  const numericNames = namesFromColumns(numericColumns && numericColumns.length ? numericColumns : columnNames);

  const [modalSelection, setModalSelection] = useState(MODEL_OPTIONS[0].value);
  const [dependentVariable, setDependentVariable] = useState(() => {
    return (numericNames && numericNames.length && numericNames[0]) || columnNames[0] || null;
  });
  const [groupBy, setGroupBy] = useState(() => {
    // allow up to 3 defaults
    return (allNames && allNames.length ? allNames : columnNames).slice(0, 3).filter((c) => c !== dependentVariable);
  });

  useEffect(() => {
    const validNumericSet = new Set(numericNames.length ? numericNames : columnNames);
    if (!dependentVariable || !validNumericSet.has(dependentVariable)) {
      const newDV = (numericNames && numericNames.length && numericNames[0]) || columnNames[0] || null;
      setDependentVariable(newDV);
    }

    const allSet = new Set(allNames);
    setGroupBy((prev) =>
      prev
        .filter((g) => allSet.has(g) && g !== dependentVariable)
        .slice(0, 3)
    );
  }, [columnNames.join("|"), numericNames.join("|"), allNames.join("|"), dependentVariable]); // use stable deps

  // Restore previously saved configuration from sessionStorage (once)
  useEffect(() => {
    if (hasRestoredRef.current) return;
    // wait until we have columns available to avoid restoring too early
    if (!columnNames || columnNames.length === 0) return;

    try {
      const stored = sessionStorage.getItem("analyticsConfig");
      if (!stored) {
        hasRestoredRef.current = true;
        return;
      }
      const parsed = JSON.parse(stored);

      const availableAll = allNames || [];
      const availableNumeric = numericNames.length ? numericNames : columnNames;

      // Validate and restore
      // Handle both string and array values (Ensemble)
      const isModelValid = (modelValue) => {
        if (Array.isArray(modelValue)) {
          // Legacy persisted array: check if it matches ENSEMBLE_MODELS
          return modelValue.length === ENSEMBLE_MODELS.length &&
                 modelValue.every((val, idx) => val === ENSEMBLE_MODELS[idx]);
        } else {
          // For strings, check if it matches any option value
          return MODEL_OPTIONS.some(o => o.value === modelValue);
        }
      };
      
      let restoredModel = modalSelection;
      if (isModelValid(parsed?.model)) {
        // If legacy array (Ensemble), map to 'ensemble' string
        if (Array.isArray(parsed.model)) {
          restoredModel = "ensemble";
        } else {
          restoredModel = parsed.model;
        }
      }
      const restoredDV = parsed?.dep && availableNumeric.includes(parsed.dep)
        ? parsed.dep
        : ((availableNumeric && availableNumeric.length && availableNumeric[0]) || columnNames[0] || null);
      const restoredGroup = Array.isArray(parsed?.group)
        ? parsed.group.filter(g => availableAll.includes(g) && g !== restoredDV).slice(0, 3)
        : groupBy;

      setModalSelection(restoredModel);
      setDependentVariable(restoredDV);
      setGroupBy(restoredGroup);
    } catch (e) {
      // ignore parsing errors but mark restored so we don't loop
      // console.error('restore analyticsConfig error', e);
    } finally {
      hasRestoredRef.current = true;
    }
  }, [columnNames.join("|"), numericNames.join("|"), allNames.join("|")]);

  // Clear groupBy whenever Benford is selected (but not when it's part of Ensemble)
  useEffect(() => {
    if (modalSelection === "benford" && !Array.isArray(modalSelection)) {
      setGroupBy([]);
    }
  }, [modalSelection]);

  // Persist configuration to sessionStorage whenever it changes
  useEffect(() => {
    try {
      const payload = {
        model: modalSelection,
        dep: dependentVariable,
        group: groupBy,
      };
      sessionStorage.setItem("analyticsConfig", JSON.stringify(payload));
    } catch (_) {
      // ignore storage errors
    }
  }, [modalSelection, dependentVariable, groupBy]);

  const onChangeModal = (val) => {
    // Guard against cleared/undefined values; fall back to default model
    if (
      val === undefined ||
      val === null ||
      (Array.isArray(val) && val.length === 0)
    ) {
      setModalSelection(MODEL_OPTIONS[0].value);
      return;
    }
    setModalSelection(val);
  };

  const onChangeDependent = (val) => {
    setDependentVariable(val);
    setGroupBy((prev) => prev.filter((g) => g !== val));
  };

  const onChangeGroupBy = (vals) => {
    const filtered = (vals || []).filter((v) => v !== dependentVariable).slice(0, 3);
    setGroupBy(filtered);
  };

  const dependentOptions = (numericNames && numericNames.length ? numericNames : columnNames).map((n) => ({ label: n, value: n }));
  const groupByOptions = (allNames && allNames.length ? allNames : columnNames).map((n) => ({ label: n, value: n }));

  return {
    modalSelection,
    dependentVariable,
    groupBy,
    onChangeModal,
    onChangeDependent,
    onChangeGroupBy,
    dependentOptions,
    groupByOptions,
    MODEL_OPTIONS,
    ENSEMBLE_MODELS,
  };
};
